import { Component, inject, model, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import {
  CompanyReportService,
  type ReportLang,
} from '../../services/company-report.service';
import { ExcelService } from '../../services/excel.service';
import { TravelBoardService } from '../../services/travel-board.service';
import { CompanyClients } from 'src/app/shared/model/companyClients';
import { PackLimitationsService } from 'src/app/shared/services/pack-limitations.service';
import { LockedButtonComponent } from 'src/app/shared/components/locked-button/locked-button.component';
import { LimitedFeatures } from 'src/app/shared/models/pack.models';
import { NotificationService } from 'src/app/core/services/notification.service';

interface SelectOption<T> {
  value: T;
  label: string;
}

@Component({
  selector: 'app-company-report-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslocoModule,
    DialogModule,
    SelectModule,
    ButtonModule,
    LockedButtonComponent,
  ],
  templateUrl: './company-report-modal.component.html',
})
export class CompanyReportModalComponent implements OnInit {
  visible = model(false);

  private companyReportService = inject(CompanyReportService);
  private excelService = inject(ExcelService);
  private translocoService = inject(TranslocoService);
  private travelBoardService = inject(TravelBoardService);
  private notificationService = inject(NotificationService);
  protected packLimitationService = inject(PackLimitationsService);

  protected years = signal<SelectOption<number>[]>([]);
  protected clients = signal<SelectOption<string>[]>([]);
  protected selectedYear = 0;
  protected selectedClient = '';
  protected isDownloading = signal(false);
  protected limitedFeature = LimitedFeatures.Client;

  /** The report is server-rendered, so its locales are the API's, not Transloco's. */
  protected langs: SelectOption<ReportLang>[] = [
    { value: 'fr', label: 'Français' },
    { value: 'en', label: 'English' },
    { value: 'nl', label: 'Nederlands' },
  ];

  /** Defaults to the UI language when the report is available in it. */
  protected selectedLang: ReportLang =
    this.langs.find(
      (lang) => lang.value === this.translocoService.getActiveLang(),
    )?.value ?? 'en';

  /**
   * Trips the report will cover. Mirrors the backend scope (drafts excluded)
   * so the preview count matches what lands in the PDF.
   */
  protected get tripCount(): number {
    let trips = this.travelBoardService.data().filter((trip) => !trip.isDraft);

    if (this.selectedYear !== 0) {
      trips = trips.filter(
        (trip) =>
          !!trip.createdAt &&
          new Date(trip.createdAt).getFullYear() === this.selectedYear,
      );
    }

    if (this.selectedClient !== '') {
      trips = trips.filter((trip) =>
        trip.companyClients?.some((client) => client === this.selectedClient),
      );
    }

    return trips.length;
  }

  ngOnInit() {
    this.loadYears();
    this.loadClients();
  }

  private loadYears() {
    this.excelService.getYears().subscribe({
      next: (years) => {
        const options = years.map((year) => ({
          value: year,
          label: year.toString(),
        }));

        this.translocoService
          .selectTranslate('companyReport.allTrips')
          .subscribe((translation) => {
            this.years.set([{ value: 0, label: translation }, ...options]);
            this.selectedYear = 0;
          });
      },
    });
  }

  private loadClients() {
    this.excelService.getClients().subscribe({
      next: (clients: CompanyClients[]) => {
        const options = clients.map((client) => ({
          value: client.id,
          label: client.name,
        }));

        this.translocoService
          .selectTranslate('companyReport.allClients')
          .subscribe((translation) => {
            this.clients.set([{ value: '', label: translation }, ...options]);
            this.selectedClient = '';
          });
      },
    });
  }

  protected download() {
    this.isDownloading.set(true);

    this.companyReportService
      .download(this.selectedLang, this.selectedYear, this.selectedClient)
      .subscribe({
        next: (response) => {
          if (!response.body) {
            this.notificationService.error(
              this.translocoService.translate('companyReport.error'),
            );
            this.isDownloading.set(false);
            return;
          }

          const blob = new Blob([response.body], { type: 'application/pdf' });
          const contentDisposition = response.headers.get(
            'Content-Disposition',
          );
          const match = contentDisposition?.match(/filename="(.+)"/);
          const fileName = match ? match[1] : 'act4travel-company-report.pdf';

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          link.click();
          window.URL.revokeObjectURL(url);

          this.isDownloading.set(false);
          this.onClose();
        },
        error: () => {
          this.notificationService.error(
            this.translocoService.translate('companyReport.error'),
          );
          this.isDownloading.set(false);
        },
      });
  }

  protected onClose() {
    this.visible.set(false);
  }
}
